/**
 * O que a app instalada de um clube mostra: o ícone e o nome por baixo dele.
 *
 * Isto existe porque o iPhone ignora os ícones do manifest e usa o
 * `apple-touch-icon`, que tem de ser um PNG quadrado. O logótipo do clube é o
 * que o dono carregou — pode ser JPEG, pode ser retangular, pode ser enorme.
 * O Cloudinary corta e converte no próprio URL, sem nada do nosso lado.
 *
 * Sem DOM aqui de propósito: a função que gera o manifest corre no servidor e
 * importa este mesmo ficheiro, para o ícone da app e o do manifest não poderem
 * divergir.
 */

const CLOUD = 'dgedcigzi';
const TRANSFORMACAO = (lado: number) => `w_${lado},h_${lado},c_fill,g_auto,f_png`;

/** Ícone da própria PadelMaster, para clubes sem logótipo. */
export const ICONE_PADELMASTER = '/icons/pwa-512.png';

export function iconeDoClube(logo: string | null | undefined, lado: number): string {
  if (!logo) return ICONE_PADELMASTER;

  // Caminho normal: o logo foi carregado pela app, logo está no nosso
  // Cloudinary. A transformação entra a seguir a /upload/.
  const marca = '/image/upload/';
  const i = logo.indexOf(marca);
  if (i !== -1) {
    return `${logo.slice(0, i + marca.length)}${TRANSFORMACAO(lado)}/${logo.slice(i + marca.length)}`;
  }

  // Qualquer outro URL (linhas antigas, ou um logo metido pelo admin do
  // Django): o Cloudinary vai buscá-lo onde estiver e converte-o na mesma.
  // ponytail: se o `fetch` estiver fechado na conta Cloudinary isto devolve
  // erro e o browser fica com o ícone por defeito — que continua a ser um
  // resultado aceitável, só não é o logótipo do clube.
  if (/^https?:\/\//.test(logo)) {
    return `https://res.cloudinary.com/${CLOUD}/image/fetch/${TRANSFORMACAO(lado)}/${encodeURIComponent(logo)}`;
  }

  return ICONE_PADELMASTER;
}

/**
 * Muda quando o clube muda de nome ou de logótipo. Vai no URL do manifest
 * (`?v=`) para o browser não servir uma versão velha a quem instala agora.
 */
export function versaoDoClube(nome: string, logo?: string | null): string {
  let h = 0;
  for (const c of `${nome}|${logo || ''}`) h = (h * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(h).toString(36);
}

/**
 * Nome para debaixo do ícone. O telemóvel corta o que passar dos ~12
 * caracteres, mas cortar a meio de uma palavra fica pior do que cortar cedo
 * ("Azen Cool Pa"), por isso corta-se entre palavras.
 */
export function nomeCurto(nome: string): string {
  if (nome.length <= 12) return nome;
  let curto = '';
  for (const palavra of nome.split(/\s+/)) {
    const junto = curto ? `${curto} ${palavra}` : palavra;
    if (junto.length > 12) break;
    curto = junto;
  }
  // Uma primeira palavra sozinha e comprida não tem por onde se cortar.
  return curto || nome.slice(0, 12);
}

/**
 * O que fazer quando a app do clube abre instalada, comparando a versão com
 * que foi instalada (`guardada`) com a de agora (`atual`):
 *
 *   registar — primeira vez: fica a saber-se com que imagem o ícone ficou
 *   calado   — o ícone ainda corresponde ao clube
 *   avisar   — o clube mudou; o ícone no ecrã é o antigo e só reinstalando muda
 */
export type DecisaoDoIcone = 'registar' | 'calado' | 'avisar';

export function decidirAviso(guardada: string | null, atual: string): DecisaoDoIcone {
  if (!guardada) return 'registar';
  return guardada === atual ? 'calado' : 'avisar';
}

/**
 * Lê o pedido de ícone `/icon/<slug>-<lado>.png`.
 *
 * O lado é opcional e vem colado ao slug, por isso há que ter cuidado com
 * clubes cujo nome acabe em números ("padel-2000"): o que se corta no fim só
 * é o tamanho se for um dos tamanhos que servimos.
 */
const LADOS = [180, 192, 512];

export function lerPedidoDeIcone(caminho: string): { slug: string; lado: number } {
  const m = caminho.match(/\/icon\/(.+)\.png$/);
  if (!m) return { slug: '', lado: 180 };
  const nome = m[1];
  const fim = nome.match(/^(.+)-(\d+)$/);
  if (fim && LADOS.includes(Number(fim[2]))) {
    return { slug: fim[1], lado: Number(fim[2]) };
  }
  return { slug: nome, lado: 180 };
}
